import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Form,
  Input,
  Button,
  Badge,
  Collapse,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Typography,
  message,
  theme,
} from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
const { RangePicker } = DatePicker;

/** Only ever one panel, but rc-collapse keys by id. */
const PANEL_KEY = "filters";

export interface FilterValues {
  request_number?: string;
  status?: (string | number)[];
  start_date_from?: string;
  start_date_to?: string;
  end_date_from?: string;
  end_date_to?: string;
  [key: string]: any;
}

interface Props {
  filters?: any[];
  statuses?: any[];
  templates?: any[];
  onFilter: (values: FilterValues) => void;
  onExport?: () => Promise<void> | void;
  /**
   * Filters the page arrived already carrying — a drill-down from Analytics
   * lands here with one status pre-applied. They seed the form *and* the
   * badge, so a list that is short because it was filtered says so while the
   * panel is still closed.
   */
  initialValues?: FilterValues;
  showRequestNumber?: boolean;
  showDates?: boolean;
  showStatus?: boolean;
  showDescription?: boolean;
  showTemplate?: boolean;
}

const stripEmpty = (values: Record<string, any>): FilterValues => {
  const result: FilterValues = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (typeof value === "string" && value.trim() === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    result[key] = value;
  });
  return result;
};

const DynamicFilterForm = ({
  filters = [],
  statuses = [],
  templates = [],
  onFilter,
  onExport,
  initialValues,
  showRequestNumber = true,
  showDates = true,
  showStatus = true,
  showDescription = false,
  showTemplate = false,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [exporting, setExporting] = useState(false);
  // Collapsed by default: every page that mounts this panel exists to show a
  // table, and the panel is two rows of controls tall before the table starts.
  const [collapsed, setCollapsed] = useState(true);
  // What is *applied*, not what is typed — a collapsed panel must still say
  // that the list below it is filtered, or a short list reads as an empty one.
  const [activeCount, setActiveCount] = useState(
    () => Object.keys(stripEmpty(initialValues ?? {})).length
  );

  const handleExport = async () => {
    if (!onExport) return;
    setExporting(true);
    try {
      await onExport();
    } catch (e) {
      message.error(t("Export failed. Please try again."));
    } finally {
      setExporting(false);
    }
  };

  const onFinish = (values: any) => {
    // Counted before normalising, so one date range is one filter rather than
    // the two bounds it turns into.
    setActiveCount(Object.keys(stripEmpty(values)).length);

    const normalized: Record<string, any> = { ...values };

    if (Array.isArray(values.start_date) && values.start_date.length === 2) {
      normalized.start_date_from = values.start_date[0]?.toISOString?.();
      normalized.start_date_to = values.start_date[1]?.toISOString?.();
    }
    delete normalized.start_date;

    if (Array.isArray(values.end_date) && values.end_date.length === 2) {
      normalized.end_date_from = values.end_date[0]?.toISOString?.();
      normalized.end_date_to = values.end_date[1]?.toISOString?.();
    }
    delete normalized.end_date;

    onFilter(stripEmpty(normalized));
  };

  const handleCleanFilters = () => {
    form.resetFields();
    setActiveCount(0);
    onFilter({});
  };

  const hasStatuses = showStatus && statuses.length > 0;
  const hasTemplates = showTemplate && templates.length > 0;
  const hasDynamicFilters = filters.length > 0;

  return (
    // A Collapse rather than a Card with a toggle button in its corner: the
    // whole header bar is the control. On a 1700px-wide panel a button at the
    // far right is half a metre of screen away from the label saying what it
    // opens, and the row between them looks inert. It also brings the
    // disclosure's keyboard handling and aria-expanded along for free.
    //
    // Hidden, not unmounted: rc-collapse keeps the panel in the DOM once it has
    // been opened, so what was typed survives a collapse, and display:none
    // takes the controls out of the tab order without any aria bookkeeping.
    <Collapse
      // The root's background is what shows through the header — antd tints it
      // grey by default, which reads as a disabled bar next to the white table.
      style={{ background: token.colorBgContainer, marginBottom: "2em" }}
      activeKey={collapsed ? [] : [PANEL_KEY]}
      onChange={(keys) =>
        setCollapsed((Array.isArray(keys) ? keys : [keys]).length === 0)
      }
      items={[
        {
          key: PANEL_KEY,
          label: (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                fontWeight: 500,
              }}
            >
              {t("Filters")}
              {collapsed && activeCount > 0 && <Badge count={activeCount} />}
            </span>
          ),
          // Plain text, not a Button: the header is already the control, and a
          // button nested inside it would be a second one saying the same thing.
          extra: (
            <Typography.Text type="secondary">
              {collapsed ? t("Show filters") : t("Hide filters")}
            </Typography.Text>
          ),
          children: (
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              initialValues={initialValues}
            >
              <Row gutter={[16, 16]}>
                {showRequestNumber && (
                  <Col key="request_number" xs={24} sm={12} md={6}>
                    <Form.Item name="request_number" label={t("Request number")}>
                      <Input placeholder={t("Request number")} allowClear />
                    </Form.Item>
                  </Col>
                )}
                {showDates && (
                  <>
                    <Col key="start_date" xs={24} sm={12} md={6}>
                      <Form.Item name="start_date" label={t("Start date")}>
                        <RangePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col key="end_date" xs={24} sm={12} md={6}>
                      <Form.Item name="end_date" label={t("End date")}>
                        <RangePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </>
                )}
                {hasStatuses && (
                  <Col key="status" xs={24} sm={12} md={6}>
                    <Form.Item name="status" label={t("Status")}>
                      <Select
                        mode="multiple"
                        placeholder={t("Please select")}
                        allowClear
                      >
                        {statuses.map((x: any) => (
                          <Select.Option key={x.id} value={x.id}>
                            {x.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
                {showDescription && (
                  <Col key="description" xs={24} sm={12} md={6}>
                    <Form.Item name="description" label={t("Description")}>
                      <Input placeholder={t("Description")} allowClear />
                    </Form.Item>
                  </Col>
                )}
                {hasTemplates && (
                  <Col key="template" xs={24} sm={12} md={6}>
                    <Form.Item name="template" label={t("Template")}>
                      <Select
                        mode="multiple"
                        placeholder={t("Please select")}
                        allowClear
                        optionFilterProp="children"
                        showSearch
                      >
                        {templates.map((x: any) => (
                          <Select.Option key={x.id} value={x.id}>
                            {x.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>
              {hasDynamicFilters && (
                <Row gutter={[16, 16]}>
                  {filters.map((filter) => (
                    <Col key={filter.field_name} xs={24} sm={12} md={8}>
                      <Form.Item name={filter.field_name} label={filter.title}>
                        <Input placeholder={filter.title} allowClear />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              )}

              <Form.Item>
                <Space wrap>
                  <Button type="primary" htmlType="submit">
                    {t("Search")}
                  </Button>
                  <Button onClick={handleCleanFilters}>{t("Clean")}</Button>
                  {onExport && (
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={handleExport}
                      loading={exporting}
                    >
                      {t("Export To Excel")}
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
          ),
        },
      ]}
    />
  );
};

export default DynamicFilterForm;
